import {
  describePresence,
  LIVENESS_POLL_MS,
  watermarkChanged,
  type EntryPresence,
  type LivenessWatermark,
} from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTimeAdminApi } from './api';

/**
 * The poll behind cross-device refresh (#1, #2) and the "someone else is on this
 * entry" indicator.
 *
 * WorkLogix had no refresh of any kind, so this is the only thing that makes a second
 * device's change visible. It is deliberately small: one request every fifteen seconds
 * that returns a watermark and a handful of names, never session data.
 */

export interface UseLivenessOptions {
  /** Payroll week being viewed, `YYYY-MM-DD`. */
  from: string | null;
  to: string | null;
  /** The entry the user currently has OPEN FOR EDITING, or null. */
  editing: string | null;
  /** Entries on screen, for which presence should be reported. */
  watching: readonly string[];
  /** Called when the server's watermark moves — refetch here. */
  onChanged: () => void;
  /** Off while the page is loading, or when there is no range yet. */
  enabled: boolean;
}

export interface UseLivenessResult {
  /** sessionId → the line to show, e.g. "EDWIN MARTINEZ is working on this entry". */
  presenceBySession: ReadonlyMap<string, string>;
  /** True when a change arrived that has not been applied yet — see the deferral. */
  pendingChange: boolean;
  /** Apply a deferred change now. */
  applyPending: () => void;
}

export function useLiveness(options: UseLivenessOptions): UseLivenessResult {
  const { from, to, editing, watching, onChanged, enabled } = options;
  const api = useTimeAdminApi();

  const [presence, setPresence] = useState<EntryPresence[]>([]);
  const [pendingChange, setPendingChange] = useState(false);

  const held = useRef<LivenessWatermark | null>(null);
  // Held in a ref so changing the callback (it is rebuilt on most renders) does not
  // tear down and restart the interval, which would reset the poll clock constantly.
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const watchingKey = watching.join(',');

  useEffect(() => {
    if (!enabled || from === null || to === null) {
      return;
    }
    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async (): Promise<void> => {
      /*
       * SKIP WHILE THE TAB IS HIDDEN. A background tab left open all week would
       * otherwise poll ~5,700 times a day for a screen nobody is looking at, and its
       * presence heartbeat would keep warning colleagues off an entry the user
       * abandoned hours ago. The next foreground poll catches up in one request.
       */
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }
      try {
        const res = await api.liveness({
          from,
          to,
          editing: editingRef.current,
          watching: watching.length > 0 ? [...watching] : [],
        });
        if (!active) {
          return;
        }
        setPresence(res.presence);
        if (watermarkChanged(held.current, res.watermark)) {
          held.current = res.watermark;
          /*
           * 🔥 NEVER REFRESH UNDER SOMEBODY'S HANDS. If the edit modal is open, the
           * user is part-way through typing a clock-out; refetching would replace the
           * row they are editing and throw the work away. The change is held and
           * applied when they close the modal — which is also the moment they would
           * want to see what somebody else did.
           */
          if (editingRef.current !== null) {
            setPendingChange(true);
          } else {
            onChangedRef.current();
          }
        }
      } catch {
        /*
         * A failed poll is not worth telling anybody about. The network blips, the
         * token refreshes, the container restarts — and the next tick recovers on its
         * own. An error banner here would cry wolf over something already self-healing.
         */
      }
    };

    const loop = (): void => {
      void tick().finally(() => {
        if (active) {
          timer = setTimeout(loop, LIVENESS_POLL_MS);
        }
      });
    };
    loop();

    // A poll the instant the tab comes back, rather than up to fifteen seconds of
    // staleness while somebody stares at it.
    const onVisible = (): void => {
      if (typeof document !== 'undefined' && !document.hidden) {
        void tick();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }

    return () => {
      active = false;
      if (timer !== null) {
        clearTimeout(timer);
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, [api, enabled, from, to, watchingKey, watching]);

  /*
   * Release presence when the page goes away.
   *
   * `pagehide` rather than `beforeunload`: it fires on mobile Safari's bfcache path,
   * where beforeunload does not, and that is exactly the phone-left-on-a-desk case
   * that would otherwise leave a badge up for the full staleness window. Best-effort —
   * the window is the backstop when this never runs at all.
   */
  useEffect(() => {
    const release = (): void => {
      void api.releaseLiveness().catch(() => undefined);
    };
    window.addEventListener('pagehide', release);
    return () => {
      window.removeEventListener('pagehide', release);
      release();
    };
  }, [api]);

  const presenceBySession = useMemo(() => {
    const byId = new Map<string, string[]>();
    for (const p of presence) {
      byId.set(p.sessionId, [...(byId.get(p.sessionId) ?? []), p.displayName]);
    }
    const out = new Map<string, string>();
    for (const [sessionId, names] of byId) {
      const line = describePresence(names);
      if (line !== '') {
        out.set(sessionId, line);
      }
    }
    return out;
  }, [presence]);

  const applyPending = useCallback(() => {
    if (pendingChange) {
      setPendingChange(false);
      onChangedRef.current();
    }
  }, [pendingChange]);

  return { presenceBySession, pendingChange, applyPending };
}
